import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateTimeSchema, reasonSchema, uuidSchema } from '../common/primitives.js';
import { localTimeSchema } from '../obligations/obligations.js';
export const NOTIFICATION_EVENT_TYPES = [
    'TASK_ASSIGNED',
    'TASK_DUE_SOON',
    'TASK_OVERDUE',
    'VOTE_REQUESTED',
    'VOTE_REMINDER',
    'MENTION',
    'BLOCKER_WAITING',
    'CRITICAL_TOPIC',
    'WEEKLY_UPDATE_DUE',
    'DECISION_FINALIZED',
    'MEETING_AGENDA_CHANGED',
    'FUNDING_CALL_DUE',
    'OBLIGATION_DUE',
    'ESCALATION',
    'SYSTEM',
];
export const notificationEventTypeSchema = z.enum(NOTIFICATION_EVENT_TYPES);
/**
 * Rendered when read: titles of restricted topics are only shown while the recipient still has
 * access (spec §18), so stored payloads hold references, never content.
 */
export const notificationSchema = z.object({
    id: uuidSchema,
    eventType: notificationEventTypeSchema,
    title: z.string(),
    detail: z.string().nullable(),
    /** In-app path, e.g. `/topics/<id>`; `null` when the target is no longer accessible. */
    link: z.string().nullable(),
    isGovernanceCritical: z.boolean(),
    createdAt: isoDateTimeSchema,
    readAt: isoDateTimeSchema.nullable(),
});
export const notificationPageSchema = cursorPageSchema(notificationSchema).extend({
    unreadCount: z.number().int(),
});
export const notificationListQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    unreadOnly: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
export const markNotificationsReadRequestSchema = z
    .object({
    ids: z.array(uuidSchema).max(100).optional(),
    all: z.boolean().optional(),
})
    .refine((value) => (value.ids !== undefined) !== (value.all === true), {
    message: 'Pass either ids or all: true',
});
export const unreadCountSchema = z.object({ unreadCount: z.number().int() });
// Channels and preferences ------------------------------------------------------------------------
export const NOTIFICATION_CHANNELS = ['IN_APP', 'EMAIL', 'PUSH', 'TELEGRAM'];
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
/**
 * Channels that deliver today. PUSH and TELEGRAM are modelled (preferences are stored) but have no
 * provider yet, so they are shown as "not available" and never receive deliveries.
 */
export const AVAILABLE_NOTIFICATION_CHANNELS = [
    'IN_APP',
    'EMAIL',
];
export function isChannelAvailable(channel) {
    return AVAILABLE_NOTIFICATION_CHANNELS.includes(channel);
}
export const NOTIFICATION_CHANNEL_LABELS = {
    IN_APP: 'In the app',
    EMAIL: 'E-mail',
    PUSH: 'Mobile push',
    TELEGRAM: 'Telegram',
};
export const NOTIFICATION_EVENT_LABELS = {
    TASK_ASSIGNED: 'A task is assigned to me',
    TASK_DUE_SOON: 'My task is due soon',
    TASK_OVERDUE: 'My task is overdue',
    VOTE_REQUESTED: 'My vote is requested',
    VOTE_REMINDER: 'Voting deadline reminder',
    MENTION: 'I am mentioned',
    BLOCKER_WAITING: 'A blocker waits for me',
    CRITICAL_TOPIC: 'A critical topic is opened',
    WEEKLY_UPDATE_DUE: 'My weekly update is due',
    DECISION_FINALIZED: 'A decision is finalized',
    MEETING_AGENDA_CHANGED: 'A meeting I take part in changes',
    FUNDING_CALL_DUE: 'A funding call is due',
    OBLIGATION_DUE: 'An obligation is due',
    ESCALATION: 'Something is escalated to me',
    SYSTEM: 'Other workspace updates',
};
/**
 * Event types that are always governance-critical (spec §7.10): they cannot be fully disabled.
 * In-app delivery is always on; e-mail stays on while it is the only available outbound channel.
 * Other events may still be flagged critical per notification (e.g. a tied vote) and then bypass
 * preferences and quiet hours as well.
 */
export const GOVERNANCE_CRITICAL_EVENT_TYPES = [
    'VOTE_REQUESTED',
    'VOTE_REMINDER',
    'CRITICAL_TOPIC',
    'DECISION_FINALIZED',
];
export function isGovernanceCriticalEvent(eventType) {
    return GOVERNANCE_CRITICAL_EVENT_TYPES.includes(eventType);
}
/** Events whose e-mail is off until the member turns it on (frequent, low-stakes). */
export const EMAIL_OFF_BY_DEFAULT_EVENT_TYPES = [
    'TASK_DUE_SOON',
    'SYSTEM',
];
export function defaultChannelEnabled(eventType, channel) {
    if (channel === 'IN_APP')
        return true;
    if (channel === 'EMAIL') {
        return !EMAIL_OFF_BY_DEFAULT_EVENT_TYPES.includes(eventType);
    }
    return false;
}
/**
 * A preference cell the member cannot switch off: in-app always, and every available outbound
 * channel of a governance-critical event while it is the only one.
 */
export function isChannelLocked(eventType, channel) {
    if (channel === 'IN_APP')
        return true;
    if (!isChannelAvailable(channel))
        return false;
    const outbound = AVAILABLE_NOTIFICATION_CHANNELS.filter((item) => item !== 'IN_APP');
    return isGovernanceCriticalEvent(eventType) && outbound.length === 1;
}
export const notificationPreferenceCellSchema = z.object({
    channel: notificationChannelSchema,
    enabled: z.boolean(),
    /** Cannot be switched off (see {@link isChannelLocked}). */
    locked: z.boolean(),
    /** The channel has a delivery provider; unavailable cells are stored but not delivered. */
    available: z.boolean(),
});
export const notificationPreferenceRowSchema = z.object({
    eventType: notificationEventTypeSchema,
    label: z.string(),
    governanceCritical: z.boolean(),
    channels: z.array(notificationPreferenceCellSchema),
});
/**
 * Quiet hours hold back non-critical e-mail until they end (in-app notifications still appear).
 * Governance-critical notifications are never held back.
 */
export const quietHoursSchema = z
    .object({
    enabled: z.boolean(),
    start: localTimeSchema.nullable(),
    end: localTimeSchema.nullable(),
})
    .refine((value) => !value.enabled || (value.start !== null && value.end !== null && value.start !== value.end), { message: 'Choose a start and an end that differ', path: ['end'] });
export const notificationSettingsSchema = z.object({
    rows: z.array(notificationPreferenceRowSchema),
    quietHours: quietHoursSchema,
    /** Quiet hours are read in this zone (the member's profile time zone). */
    timeZone: z.string(),
    /** 0 when the member never saved settings. */
    version: z.number().int().min(0),
});
export const updateNotificationSettingsRequestSchema = z.object({
    version: z.number().int().min(0),
    preferences: z
        .array(z.object({
        eventType: notificationEventTypeSchema,
        channel: notificationChannelSchema,
        enabled: z.boolean(),
    }))
        .max(NOTIFICATION_EVENT_TYPES.length * NOTIFICATION_CHANNELS.length),
    quietHours: quietHoursSchema,
});
// Workspace delivery policy ------------------------------------------------------------------------
/**
 * Workspace-level delivery policy (spec §17: intervals are workspace settings; bundle to avoid spam
 * while critical governance actions stay prominent).
 */
export const notificationPolicySchema = z.object({
    /** Non-critical e-mails wait this long so several events go out as one message (0 = immediately). */
    emailBundleMinutes: z.number().int().min(0).max(240),
    /** Re-send an unread governance-critical notification after this many hours (0 = never). */
    criticalReminderHours: z.number().int().min(0).max(336),
    /** Upper bound of such reminders per notification. */
    maxCriticalReminders: z.number().int().min(0).max(10),
});
/** Documented defaults used until the workspace saves its own values (see ADR-0006). */
export const DEFAULT_NOTIFICATION_POLICY = {
    emailBundleMinutes: 10,
    criticalReminderHours: 24,
    maxCriticalReminders: 2,
};
export const notificationPolicySettingsSchema = notificationPolicySchema.extend({
    isDefault: z.boolean(),
    version: z.number().int().min(0),
});
export const updateNotificationPolicyRequestSchema = notificationPolicySchema.extend({
    version: z.number().int().min(0),
    reason: reasonSchema,
});
//# sourceMappingURL=notifications.js.map